import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, MessageSquare, CheckCircle, ArrowRight, Plus, X, Building2, Zap } from 'lucide-react'
import { WaveIcon } from '@/components/common/WaveIcon'
import { ErrorFallback } from '@/components/common/ErrorFallback'
import { useEngineRequest } from '@/hooks/useEngineRequest'
import { engineApiClient } from '@/lib/apiClient'
import type { ProjectStage, ProjectSummary } from '@/lib/contracts'
import { formatRelativeTime } from '@/utils/formatters'
import { getProjectProgress } from '@/lib/projectProgress'

// ─── Onboarding banner ───────────────────────────────────────────────────────

const ONBOARDING_DISMISSED_KEY = 'searchline:onboarding_dismissed'

const onboardingSteps = [
  {
    num: 1,
    icon: Building2,
    title: 'Set up your company',
    description: 'Add your company details in Settings so Erica can personalise outreach.',
    href: '/settings',
  },
  {
    num: 2,
    icon: Search,
    title: 'Create your first search',
    description: 'Tell Erica who you\u2019re looking for — role, seniority, location, and brief.',
    href: '/new-project',
  },
  {
    num: 3,
    icon: Zap,
    title: 'Brief Erica',
    description: 'Walk Erica through your requirements so she can start sourcing and outreach.',
    href: '/new-project',
  },
]

const OnboardingBanner = ({ onDismiss }: { onDismiss: () => void }) => {
  const navigate = useNavigate()
  return (
    <div className="mx-auto max-w-7xl px-6 mb-8">
      <div className="bg-[#233D4C] border border-[#FD802E]/30 rounded-xl overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a4a5c] bg-[#02182B]/50">
          <div className="flex items-center gap-3">
            <WaveIcon size="sm" animate className="text-[#FD802E]" />
            <span className="text-white font-semibold text-sm">Welcome to Searchline — let\u2019s get you set up</span>
          </div>
          <button
            onClick={onDismiss}
            className="text-gray-500 hover:text-white transition-colors"
            aria-label="Dismiss onboarding"
          >
            <X size={16} />
          </button>
        </div>

        {/* Steps */}
        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {onboardingSteps.map((step, idx) => (
            <div key={step.num} className="flex gap-3">
              {/* Step number */}
              <div className="shrink-0 w-7 h-7 rounded-full bg-[#FD802E]/15 border border-[#FD802E]/40 flex items-center justify-center">
                <span className="text-[#FD802E] text-xs font-bold">{step.num}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <step.icon size={13} className="text-[#FD802E] shrink-0" />
                  <span className="text-white text-sm font-semibold">{step.title}</span>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed mb-2">{step.description}</p>
                <button
                  onClick={() => navigate(step.href)}
                  className="text-[#FD802E] text-xs font-medium hover:text-[#ff8f45] transition-colors flex items-center gap-1"
                >
                  {idx === 0 ? 'Go to Settings' : idx === 1 ? 'Start new project' : 'Begin setup'}
                  <ArrowRight size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Stage routing — maps DB stage to the correct route segment ───────────────
// This is the ONLY place this mapping lives. All navigation uses this.

export const stageRoute: Record<ProjectStage, string> = {
  'upload':         'upload',
  'context-review': 'upload',      // legacy — maps to upload
  'intake':         'intake',
  'deep-probe':     'intake',      // legacy — merged into intake session
  'confirmation':   'confirmation',
  'outputs':        'output',
  'dashboard':      'dashboard',
}

// ─── Status pill styles ───────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft:        'bg-gray-500/10 text-gray-400',
  'in-progress':'bg-[#FD802E]/10 text-[#FD802E]',
  ready:        'bg-blue-500/10 text-blue-400',
  live:         'bg-green-500/10 text-green-400',
}

// ─── Feature cards ────────────────────────────────────────────────────────────

const featureCards = [
  {
    icon: Search,
    title: 'AI Candidate Sourcing',
    description: 'Tell Erica what you\'re looking for. She searches, scores and ranks candidates against your brief — surfacing the best fits from our 200M+ contact database or your own uploaded CV pool.',
  },
  {
    icon: MessageSquare,
    title: 'Automated Pre-Screening',
    description: 'Erica reaches out to every candidate with a personalised email and conducts an async AI interview via magic link. Candidates complete it in their own time — you get structured results, scores and recommendations.',
  },
  {
    icon: CheckCircle,
    title: 'Shortlist in Hours, Not Weeks',
    description: 'While you focus on clients, Erica works around the clock. By the time you review the pipeline, candidates have already been sourced, contacted and pre-screened. Your shortlist arrives ready to present.',
  },
]

// ─── Pipeline funnel stat block ───────────────────────────────────────────────

const PipelineStat = ({
  count,
  label,
  color,
}: {
  count: number
  label: string
  color: string
}) => (
  <div className="flex flex-col items-center gap-0.5 min-w-0">
    <span className={`text-xl font-bold leading-none ${color}`}>{count}</span>
    <span className="text-gray-500 text-[10px] leading-none mt-1">{label}</span>
  </div>
)

// ─── Live project card ────────────────────────────────────────────────────────

const LiveProjectCard = ({
  project,
  onOpen,
}: {
  project: ProjectSummary
  onOpen: () => void
}) => {
  const counts = project.pipelineCounts ?? { new: 0, outreach: 0, shortlist: 0, interview: 0 }

  let actionPrompt: React.ReactNode
  if (counts.new > 0) {
    actionPrompt = (
      <span className="text-[#FD802E] font-medium">
        {counts.new} candidates ready to review →
      </span>
    )
  } else if (counts.outreach > 0) {
    actionPrompt = (
      <span className="text-blue-400">{counts.outreach} candidates in outreach</span>
    )
  } else if (counts.shortlist > 0) {
    actionPrompt = (
      <span className="text-purple-400">{counts.shortlist} candidates shortlisted</span>
    )
  } else {
    actionPrompt = <span className="text-gray-500">Pipeline active</span>
  }

  return (
    <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5 flex flex-col gap-4 hover:border-[#FD802E]/30 transition-colors">

      {/* Header: title + Live badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm leading-snug truncate">
            {project.title}
          </h3>
        </div>
        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400">
          <WaveIcon size="sm" />
          Live
        </span>
      </div>

      {/* Pipeline funnel */}
      <div className="flex items-center justify-between gap-1 py-2">
        <PipelineStat count={counts.new}       label="New"       color="text-[#FD802E]" />
        <span className="text-gray-600 text-xs">→</span>
        <PipelineStat count={counts.outreach}  label="Outreach"  color="text-blue-400" />
        <span className="text-gray-600 text-xs">→</span>
        <PipelineStat count={counts.shortlist} label="Shortlist" color="text-purple-400" />
        <span className="text-gray-600 text-xs">→</span>
        <PipelineStat count={counts.interview} label="Interview" color="text-green-400" />
      </div>

      {/* Action prompt */}
      <p className="text-xs">{actionPrompt}</p>

      {/* Meta */}
      <p className="text-gray-500 text-xs">{formatRelativeTime(project.lastUpdated)}</p>

      {/* CTA */}
      <button
        onClick={onOpen}
        className="w-full flex items-center justify-center gap-1.5 font-semibold text-xs py-2.5 rounded-lg transition-colors bg-[#FD802E] text-[#233D4C] hover:bg-[#ff8f45]"
      >
        View Pipeline
        <ArrowRight size={13} />
      </button>
    </div>
  )
}

// ─── Setup / in-progress project card ────────────────────────────────────────

const SetupProjectCard = ({
  project,
  onOpen,
}: {
  project: ProjectSummary
  onOpen: () => void
}) => {
  const progress = getProjectProgress(project.stage)
  const statusStyle = STATUS_STYLES[progress.statusVariant]

  return (
    <div className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5 flex flex-col gap-4 hover:border-[#FD802E]/30 transition-colors">

      {/* Title + status pill */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-white font-semibold text-sm leading-snug flex-1 min-w-0 truncate">
          {project.title}
        </h3>
        <span className={`shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>
          {progress.statusLabel}
        </span>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Setup progress</span>
          <span className={`font-semibold ${progress.percent === 100 ? 'text-green-400' : 'text-[#FD802E]'}`}>
            {progress.percent}%
          </span>
        </div>
        <div className="h-1.5 bg-[#02182B] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${progress.percent === 100 ? 'bg-green-500' : 'bg-[#FD802E]'}`}
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      </div>

      {/* 4-step checklist */}
      <ul className="space-y-2">
        {progress.steps.map((step) => (
          <li key={step.label} className="flex items-center gap-2.5 text-xs">
            <span className={`
              w-4 h-4 rounded-full border flex items-center justify-center shrink-0
              ${step.complete
                ? 'bg-green-500/15 border-green-500/40'
                : step.active
                  ? 'border-[#FD802E]/60 bg-[#FD802E]/10'
                  : 'border-[#2a4a5c] bg-transparent'
              }
            `}>
              {step.complete
                ? <CheckCircle size={9} className="text-green-400" />
                : step.active
                  ? <span className="w-1.5 h-1.5 rounded-full bg-[#FD802E]" />
                  : null
              }
            </span>
            <span className={`leading-none ${
              step.complete
                ? 'text-gray-500 line-through'
                : step.active
                  ? 'text-white font-medium'
                  : 'text-gray-600'
            }`}>
              {step.label}
            </span>
            {step.active && (
              <span className="ml-auto text-[#FD802E] font-medium">← next</span>
            )}
          </li>
        ))}
      </ul>

      {/* Meta */}
      <p className="text-gray-500 text-xs">{formatRelativeTime(project.lastUpdated)}</p>

      {/* CTA */}
      <button
        onClick={onOpen}
        className="w-full flex items-center justify-center gap-1.5 font-semibold text-xs py-2.5 rounded-lg transition-colors bg-[#02182B] border border-[#2a4a5c] text-white hover:border-[#FD802E]/40 hover:text-[#FD802E]"
      >
        {progress.percent === 0 ? 'Start Setup' : 'Continue'}
        <ArrowRight size={13} />
      </button>
    </div>
  )
}

// ─── Project card — routes to live or setup variant ──────────────────────────

const ProjectCard = ({
  project,
  onOpen,
}: {
  project: ProjectSummary
  onOpen: () => void
}) => {
  const progress = getProjectProgress(project.stage)
  const isLive = progress.statusVariant === 'live'

  return isLive
    ? <LiveProjectCard project={project} onOpen={onOpen} />
    : <SetupProjectCard project={project} onOpen={onOpen} />
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const Home = () => {
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = useEngineRequest(() => engineApiClient.listProjects(), [])
  const handleRetry = useCallback(() => refetch(), [refetch])
  const projects = data?.projects ?? []

  // Onboarding: show when user has 0 projects and hasn't dismissed
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true'
  )
  const handleDismiss = () => {
    setDismissed(true)
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true')
  }
  // Auto-dismiss once they have a project (store the flag so it doesn't flash on refresh)
  const showOnboarding = !isLoading && !dismissed && projects.length === 0

  return (
    <div className="min-h-screen bg-[#02182B]">

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl font-bold text-white leading-tight">
          Find, screen and shortlist top talent — automatically.
        </h1>
        <p className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto">
          Searchline runs your entire search process. Erica sources candidates, writes personalised outreach, conducts AI pre-screen interviews, and delivers a ranked shortlist — so you only spend time on people worth interviewing.
        </p>
        <button
          onClick={() => navigate('/new-project')}
          className="mt-8 inline-flex items-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold px-8 py-4 rounded-lg hover:bg-[#ff8f45] transition-colors"
        >
          <Plus size={20} />
          Start New Project
        </button>
      </div>

      {/* Onboarding banner — first-run only */}
      {showOnboarding && <OnboardingBanner onDismiss={handleDismiss} />}

      {/* Feature cards */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featureCards.map((card) => (
            <div key={card.title} className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-6">
              <div className="w-10 h-10 bg-[#FD802E]/10 rounded-lg flex items-center justify-center mb-4">
                <card.icon size={20} className="text-[#FD802E]" />
              </div>
              <h3 className="text-white font-semibold">{card.title}</h3>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent projects */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white">Your Searches</h2>
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#233D4C] rounded-xl border border-[#2a4a5c] p-5 animate-pulse h-52" />
            ))}
          </div>
        ) : error ? (
          <ErrorFallback onRetry={handleRetry} />
        ) : projects.length === 0 ? (
          <div className="bg-[#233D4C] rounded-xl border border-dashed border-[#2a4a5c] p-12 text-center">
            <p className="text-gray-400 text-sm">No projects yet.</p>
            <button
              onClick={() => navigate('/new-project')}
              className="mt-4 bg-[#FD802E] text-[#233D4C] font-semibold px-6 py-3 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm"
            >
              Start your first project
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={() => navigate(`/project/${project.id}/${stageRoute[project.stage]}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
